import { useEffect, useState, useCallback } from "react";
import { adminIntegrations } from "@/lib/api";
import { toast } from "sonner";
import { Plug, Database, Clock, Save, Play, Download, CheckCircle2, XCircle, Search, RefreshCw, Coins } from "lucide-react";

const inputCls = "w-full px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#FFBF00]/50 focus:outline-none text-sm";
const btnGold = "px-4 py-2 rounded-lg bg-[#FFBF00] text-black font-semibold text-sm blocky-shadow-black hover-blocky inline-flex items-center gap-2 disabled:opacity-50";
const btnGlass = "px-4 py-2 rounded-lg glass text-sm hover-blocky inline-flex items-center gap-2 disabled:opacity-50";

function Field({ label, children }) {
  return <label className="text-xs text-white/50 block">{label}{children}</label>;
}

export default function AdminIntegrations() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState({});
  const [testId, setTestId] = useState("");
  const [testOut, setTestOut] = useState(null);

  const load = useCallback(() => adminIntegrations.get().then(setCfg).catch(() => toast.error("Load failed (check admin token)")), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      // don't resend masked secrets unchanged
      if (payload.wc_consumer_secret === "••••••••") delete payload.wc_consumer_secret;
      if (payload.mysql_password === "••••••••") delete payload.mysql_password;
      delete payload.wc_consumer_secret_set; delete payload.mysql_password_set;
      delete payload.last_auto_sync_at; delete payload.last_auto_sync_result;
      const r = await adminIntegrations.save(payload);
      setCfg(r); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const run = async (key, fn, label) => {
    setBusy(key);
    try { const r = await fn(); setResult((p) => ({ ...p, [key]: r })); toast[r.ok === false ? "error" : "success"](r.ok === false ? (r.error || "Failed") : (label || "Done")); }
    catch (e) { const err = e?.response?.data?.detail || "Failed"; setResult((p) => ({ ...p, [key]: { ok: false, error: err } })); toast.error(err); }
    finally { setBusy(""); }
  };

  const runTest = async () => {
    if (!testId.trim()) return;
    setBusy("dctest");
    try { setTestOut(await adminIntegrations.testDcCoins(testId.trim())); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  if (!cfg) return <div className="text-white/40 py-8">Loading…</div>;
  const R = ({ k }) => result[k] ? (result[k].ok === false
    ? <span className="text-red-400 text-xs inline-flex items-center gap-1"><XCircle size={13} /> {result[k].error}</span>
    : <span className="text-emerald-400 text-xs inline-flex items-center gap-1"><CheckCircle2 size={13} /> {JSON.stringify(result[k])}</span>) : null;

  return (
    <div className="space-y-8" data-testid="admin-integrations">
      {/* WooCommerce */}
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-xl mb-4 flex items-center gap-2"><Plug size={18} className="text-[#FFBF00]" /> WooCommerce (Casper Store)</h3>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Field label="Store URL"><input className={inputCls} value={cfg.wc_store_url || ""} onChange={(e) => set("wc_store_url", e.target.value)} placeholder="https://store.lostdrkc.in" data-testid="woo-url" /></Field>
          <Field label="Consumer Key (ck_…)"><input className={inputCls} value={cfg.wc_consumer_key || ""} onChange={(e) => set("wc_consumer_key", e.target.value)} data-testid="woo-ck" /></Field>
          <Field label="Consumer Secret (cs_…)"><input className={inputCls} type="password" value={cfg.wc_consumer_secret || ""} onChange={(e) => set("wc_consumer_secret", e.target.value)} data-testid="woo-cs" /></Field>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <button className={btnGold} onClick={save} disabled={saving} data-testid="integrations-save"><Save size={15} /> Save</button>
          <button className={btnGlass} onClick={() => run("woo", adminIntegrations.wooTest, "Connected")} disabled={busy === "woo"} data-testid="woo-test"><Play size={15} /> Test Connection</button>
          <button className={btnGlass} onClick={() => run("woo_p", adminIntegrations.wooImportProducts, "Products imported")} disabled={busy === "woo_p"} data-testid="woo-import-products"><Download size={15} /> Import Products</button>
          <button className={btnGlass} onClick={() => run("woo_o", adminIntegrations.wooImportOrders, "Orders imported")} disabled={busy === "woo_o"} data-testid="woo-import-orders"><Download size={15} /> Import Orders</button>
        </div>
        <div className="mt-2 space-y-1"><R k="woo" /><R k="woo_p" /><R k="woo_o" /></div>
      </div>

      {/* MySQL bridge */}
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-xl mb-1 flex items-center gap-2"><Database size={18} className="text-[#FFBF00]" /> Game-Server MySQL Bridge</h3>
        <p className="text-xs text-white/50 mb-4">On every completed order, delivery commands are written into your server's MySQL table so the Minecraft/Discord plugin can run them.</p>
        <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={!!cfg.mysql_enabled} onChange={(e) => set("mysql_enabled", e.target.checked)} data-testid="mysql-enabled" /> Enable MySQL delivery sync</label>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Field label="Host / IP"><input className={inputCls} value={cfg.mysql_host || ""} onChange={(e) => set("mysql_host", e.target.value)} placeholder="123.45.67.89" data-testid="mysql-host" /></Field>
          <Field label="Port"><input type="number" className={inputCls} value={cfg.mysql_port || 3306} onChange={(e) => set("mysql_port", Number(e.target.value))} data-testid="mysql-port" /></Field>
          <Field label="Database"><input className={inputCls} value={cfg.mysql_database || ""} onChange={(e) => set("mysql_database", e.target.value)} placeholder="wordpress_db" data-testid="mysql-db" /></Field>
          <Field label="User"><input className={inputCls} value={cfg.mysql_user || ""} onChange={(e) => set("mysql_user", e.target.value)} data-testid="mysql-user" /></Field>
          <Field label="Password"><input type="password" className={inputCls} value={cfg.mysql_password || ""} onChange={(e) => set("mysql_password", e.target.value)} data-testid="mysql-pass" /></Field>
          <Field label="Orders Table"><input className={inputCls} value={cfg.mysql_orders_table || ""} onChange={(e) => set("mysql_orders_table", e.target.value)} placeholder="wp_lostdc_store_orders" data-testid="mysql-table" /></Field>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className={btnGold} onClick={save} disabled={saving}><Save size={15} /> Save</button>
          <button className={btnGlass} onClick={() => run("sql", adminIntegrations.mysqlTest)} disabled={busy === "sql"} data-testid="mysql-test"><Play size={15} /> Test Connection</button>
          <button className={btnGlass} onClick={() => run("sqlt", adminIntegrations.mysqlEnsureTable, "Table ready")} disabled={busy === "sqlt"} data-testid="mysql-ensure"><Database size={15} /> Create/Ensure Table</button>
        </div>
        <div className="mt-2 space-y-1"><R k="sql" /><R k="sqlt" /></div>
      </div>

      {/* Auto-sync schedule */}
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-xl mb-1 flex items-center gap-2"><Clock size={18} className="text-[#FFBF00]" /> DC Coin Auto-Sync Schedule</h3>
        <p className="text-xs text-white/50 mb-4">Your servers push balances to <code className="text-[#FFBF00]">/api/coins/sync/ingest</code>; this scheduler credits wallets automatically on an interval — no manual sync needed.</p>
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!cfg.sync_enabled} onChange={(e) => set("sync_enabled", e.target.checked)} data-testid="sync-enabled" /> Enable auto-sync</label>
          <Field label="Interval (minutes)"><input type="number" min={5} className={inputCls + " w-32"} value={cfg.sync_interval_min || 60} onChange={(e) => set("sync_interval_min", Number(e.target.value))} data-testid="sync-interval" /></Field>
          <button className={btnGold} onClick={save} disabled={saving} data-testid="sync-save"><Save size={15} /> Save Schedule</button>
        </div>
        {cfg.last_auto_sync_at && (
          <div className="text-xs text-white/50 mt-3 inline-flex items-center gap-2"><RefreshCw size={12} /> Last auto-sync: {new Date(cfg.last_auto_sync_at).toLocaleString()} — {JSON.stringify(cfg.last_auto_sync_result)}</div>
        )}
      </div>

      {/* Test DC Coins */}
      <div className="glass rounded-2xl p-6" data-testid="admin-test-dccoins">
        <h3 className="font-display text-xl mb-1 flex items-center gap-2"><Coins size={18} className="text-[#FFBF00]" /> Test DC Coins (verify a LostDC ID)</h3>
        <p className="text-xs text-white/50 mb-4">Enter a verified LostDC ID to confirm the wallet + ledger work here and cross-check the linked WooCommerce customer/orders.</p>
        <div className="flex gap-2 mb-4">
          <input className={inputCls + " max-w-xs"} placeholder="e.g. Notch" value={testId} onChange={(e) => setTestId(e.target.value)} data-testid="dctest-input" />
          <button className={btnGold} onClick={runTest} disabled={busy === "dctest"} data-testid="dctest-run"><Search size={15} /> Test</button>
        </div>
        {testOut && (
          <div className="text-sm space-y-3" data-testid="dctest-output">
            {testOut.found ? (
              <>
                <div className="p-3 rounded-xl bg-[#FFBF00]/10 border border-[#FFBF00]/25">
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    <span>LostDC ID: <b className="text-[#FFBF00]">{testOut.wallet.lostdc_id}</b></span>
                    <span>Balance: <b className="text-[#FFBF00]">{testOut.wallet.dc_coin} DC</b></span>
                    <span>PIN set: {testOut.wallet.has_pin ? "✓" : "✗"}</span>
                    <span>Email: {testOut.wallet.email}</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Recent transactions ({testOut.transactions.length})</div>
                  {testOut.transactions.slice(0, 8).map((t) => (
                    <div key={t.id} className="flex justify-between text-xs py-1 border-b border-white/5">
                      <span>{t.type} · {t.type === "transfer" ? (t.direction === "sent" ? `→ ${t.receiver_lostdc}` : `← ${t.sender_lostdc}`) : t.note}</span>
                      <span className="text-[#FFBF00]">{t.amount} DC</span>
                    </div>
                  ))}
                </div>
                {testOut.woocommerce && (
                  <div className="p-3 rounded-xl bg-black/40 border border-white/10 text-xs">
                    <div className="uppercase tracking-widest text-white/40 mb-1">WooCommerce lookup {testOut.woocommerce.ok ? "✓" : "✗"}</div>
                    {testOut.woocommerce.customer ? <div>Customer: {testOut.woocommerce.customer.email} — {testOut.woocommerce.orders?.length || 0} order(s)</div> : <div className="text-white/40">No matching WooCommerce customer.</div>}
                  </div>
                )}
              </>
            ) : <div className="text-red-400">No wallet found for that LostDC ID.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
